'use client';

import React, { useEffect } from 'react';

import { useNativeDetect } from './use-native-detect';

interface CapacitorGlobal {
  Plugins?: {
    SplashScreen?: {
      hide: (opts?: { fadeOutDuration?: number }) => void;
    };
  };
}

/**
 * Conditionally renders native or web auth shells.
 *
 * Default-to-web: webContent is rendered on the server and on the first
 * client paint so web users get a fully SSR'd page with no blank flash.
 * Once useNativeDetect confirms the Capacitor runtime, the gate swaps to
 * nativeContent. Native users see a brief frame of web chrome before the
 * swap, which is acceptable because the native shell paints over it with
 * the matching primary color anyway.
 *
 * This component is used both in auth/layout.tsx (for shell switching) and
 * within sign-in/sign-up content components (for page content switching).
 */
export function NativeAuthGate({
  nativeContent,
  webContent,
}: {
  nativeContent: React.ReactNode;
  webContent: React.ReactNode;
  primaryColor?: string;
  appBackgroundColor?: string;
}) {
  const { isNative, isReady } = useNativeDetect();

  useEffect(() => {
    if (isReady && isNative) {
      const cap = (window as unknown as Record<string, unknown>).Capacitor as
        | CapacitorGlobal
        | undefined;

      try {
        cap?.Plugins?.SplashScreen?.hide({ fadeOutDuration: 300 });
      } catch {
        // The bridge can be present before the plugin is ready.
      }
    }
  }, [isReady, isNative]);

  if (isReady && isNative) {
    return <>{nativeContent}</>;
  }

  return <>{webContent}</>;
}
